import { Button, Modal, Radio, Collapse } from "antd";
import classNames from "classnames";
import { useControlVisible } from "components/hooks";
import DangerousMessage from "components/Message/DangerousMessage";
import IntlMessages from "components/Message/IntlMessages";
import ModalTemplate1 from "components/Modal/ModalTemplate/ModalTemplate1";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import {
  DEFAULT_MESSAGE_IDS,
  Popup,
  SelfCertificationType,
  SELF_CERT_TYPE_KEY,
} from "./helper";

function resolveTitleNode(overrideVal, defaultId) {
  if (typeof overrideVal === "function") return overrideVal();
  const id = overrideVal !== undefined && overrideVal !== null ? overrideVal : defaultId;
  return id ? <IntlMessages id={id} /> : null;
}

function resolveMessageSlot(overrideVal, defaultVal) {
  if (typeof overrideVal === "function") return overrideVal();
  return overrideVal !== undefined ? overrideVal : defaultVal;
}

function NonFaceCrsPopup({
  crs,
  countryOfTaxList,
  onContinue,
  onCancel,
  selfCertType,
  messageOverrides,
  noTitleForReject,
}) {
  const certKey = SELF_CERT_TYPE_KEY[selfCertType];
  const intl = useIntl();
  const [answer, setAnswer] = useState(null);
  const [popupCrsVisible, , [showPopupCrs, hidePopupCrs]] = useControlVisible();
  const [rejectPopupVisible, , [showPopupReject, hidePopupReject]] = useControlVisible();

  useEffect(() => {
    const showCrsPopup = crs === Popup.SelfCertification || crs === Popup.Reject;
    if (showCrsPopup) {
      if (crs === Popup.SelfCertification) {
        setAnswer(null);
        showPopupCrs();
      } else {
        showPopupReject();
      }
    } else {
      hidePopupCrs();
      hidePopupReject();
    }
  }, [crs, hidePopupCrs, hidePopupReject, showPopupCrs, showPopupReject]);

  const hidePopup = (popup) => {
    if (popup === Popup.SelfCertification) {
      hidePopupCrs();
    } else {
      hidePopupReject();
    }

    if (onCancel) {
      onCancel();
    }
  };

  const onConfirm = () => {
    hidePopupCrs();

    if (answer) {
      showPopupReject();
      return;
    }

    onContinue();
  };

  const certKeyOrFallback = certKey ?? "property";
  const defaultTitleSelfCert = DEFAULT_MESSAGE_IDS.titleSelfCert[certKeyOrFallback];
  const titleSelfCertNode = resolveTitleNode(
    messageOverrides?.title?.[certKeyOrFallback],
    defaultTitleSelfCert
  );

  const renderSelfCertificationModal = () => {
    return (
      <Modal
        visible={popupCrsVisible}
        title={false}
        footer={false}
        maskClosable={false}
        closable={false}
        style={{ top: 20 }}
        destroyOnClose
      >
        <ModalTemplate1
          title={titleSelfCertNode}
          titleClass="align-items-baseline"
          body={
            <NonFaceCrsContent
              answer={answer}
              setAnswer={setAnswer}
              countryOfTaxList={countryOfTaxList}
              selfCertType={selfCertType}
              messageOverrides={messageOverrides}
            />
          }
          footer={
            <div className="d-flex gap-3">
              <Button className="btn button-ktb-line w-100" onClick={() => hidePopup(Popup.SelfCertification)}>
                <IntlMessages id="global.cancel" />
              </Button>
              <Button className="btn button-ktb-main w-100" onClick={onConfirm} disabled={answer === null}>
                <IntlMessages id="global.confirm" />
              </Button>
            </div>
          }
          onClose={() => hidePopup(Popup.SelfCertification)}
        />
      </Modal>
    );
  };

  const defaultTitleReject = DEFAULT_MESSAGE_IDS.titleRejectCert[certKeyOrFallback];
  const titleRejectNode = resolveTitleNode(
    messageOverrides?.titleReject?.[certKeyOrFallback],
    defaultTitleReject
  );

  const renderRejectModal = () => {
    // const messageProps = selfCertType === SelfCertificationType.Property ? { className: "text-center" } : {};
    const messageProps = {};
    const rejectMsgOverride = messageOverrides?.messageRejectCert?.[certKeyOrFallback];
    const defaultRejectId = DEFAULT_MESSAGE_IDS.messageRejectCert[certKeyOrFallback];
    const rejectBodyContent =
      typeof rejectMsgOverride === "function"
        ? rejectMsgOverride()
        : intl.formatMessage({ id: rejectMsgOverride ?? defaultRejectId });

    const popupProps = noTitleForReject ? {} : { title: titleRejectNode, onClose: () => hidePopup(Popup.Reject) };

    return (
      <Modal
        visible={rejectPopupVisible}
        title={false}
        footer={false}
        maskClosable={false}
        closable={false}
        destroyOnClose
      >
        <ModalTemplate1
          titleClass="align-items-baseline"
          body={
            <NonFaceCRSCase1 className="pb-3">
              {typeof rejectMsgOverride === "function" ? (
                <div style={{ fontWeight: 600 }} {...messageProps}>
                  {rejectBodyContent}
                </div>
              ) : (
                <DangerousMessage.Div
                  html={rejectBodyContent}
                  style={{ fontWeight: 600 }}
                  {...messageProps}
                />
              )}
              {crs === "02" && countryOfTaxList?.length > 0 && (
                <div className="collapse-nonface mt-3">
                  <Collapse ghost>
                    <Panel header={<IntlMessages id="non-face-crs.address" />}>
                      {countryOfTaxList.map((country) => (
                        <CountryTax key={country.countryCode} country={country} className="mt-3" />
                      ))}
                    </Panel>
                  </Collapse>
                </div>
              )}
              <div className="d-flex justify-content-center align-items-center mt-4">
                <Button className="btn button-ktb-line w-100" onClick={() => hidePopup(Popup.Reject)}>
                  <IntlMessages id="global.close" />
                </Button>
              </div>
            </NonFaceCRSCase1>
          }
          {...popupProps}
        />
      </Modal>
    );
  };

  const { Panel } = Collapse;
  return (
    <>
      {/* Self Certification Popup */}
      {renderSelfCertificationModal()}

      {/* Reject Modal */}
      {renderRejectModal()}
    </>
  );
}

export default NonFaceCrsPopup;

const NonFaceCrsContent = ({
  answer,
  setAnswer,
  countryOfTaxList,
  selfCertType,
  messageOverrides,
}) => {
  const intl = useIntl();
  const certKey = SELF_CERT_TYPE_KEY[selfCertType];
  const certKeyOrFallback = certKey ?? "property";
  const defaultMessages = DEFAULT_MESSAGE_IDS.messageSelfCert[certKeyOrFallback] || {};
  const overrideMap = messageOverrides?.messageSelfCert?.[certKeyOrFallback] || {};
  const message_1 = resolveMessageSlot(
    overrideMap?.[0],
    defaultMessages?.[0] ?? null
  );
  const message_2 = resolveMessageSlot(overrideMap?.[1], defaultMessages?.[1]);
  const message_3 = resolveMessageSlot(overrideMap?.[2], defaultMessages?.[2]);

  const renderMsg1 = () => {
    if (message_1 == null) return null;
    if (typeof message_1 === "string") {
      return (
        <DangerousMessage.Div
          html={intl.formatMessage({ id: message_1 })}
          style={{ fontSize: 16, fontWeight: 600, color: "var( --ktb-navy-blue-main)", lineHeight: 1.7 }}
        />
      );
    }
    return (
      <div style={{ fontSize: 16, fontWeight: 600, color: "var( --ktb-navy-blue-main)", lineHeight: 1.7 }}>
        {message_1}
      </div>
    );
  };

  const renderMsg2 = () => {
    if (message_2 == null) return null;
    if (typeof message_2 === "string") {
      return (
        <small className="color-primary" style={{ fontSize: 13 }}>
          <IntlMessages id={message_2} />
        </small>
      );
    }
    return <small className="color-primary" style={{ fontSize: 13 }}>{message_2}</small>;
  };

  const renderMsg3 = () => {
    if (message_3 == null) return null;
    if (typeof message_3 === "string") {
      return <IntlMessages id={message_3} />;
    }
    return message_3;
  };

  return (
    <div className="pb-3">
      {message_1 != null && renderMsg1()}

      <div className={classNames({ "mt-3": message_1 != null })} style={{ color: "#5291bc" }}>
        {renderMsg2()}
      </div>

      <div className="color-primary-navy-blue">
        {renderMsg3()}
      </div>

      <Radio.Group className="mt-3" value={answer}>
        <Radio value={true} className="me-4" onClick={() => setAnswer(true)}>
          <IntlMessages id="global.yes" />
        </Radio>
        <Radio value={false} onClick={() => setAnswer(false)}>
          <IntlMessages id="global.no" />
        </Radio>
      </Radio.Group>

      {countryOfTaxList?.length > 0 && (
        <>
          <div className="color-label kt-font-b font-14 mt-3 mb-1">
            <IntlMessages id="non-face-crs.address.section" />
          </div>
          {countryOfTaxList.map((country, i) => (
            <CountryTax key={country.countryCode} country={country} className={classNames({ "mt-3": i !== 0 })} />
          ))}
        </>
      )}

      <div className="mt-3 p-3" style={{ backgroundColor: "#f4f4f5", color: "var( --ktb-navy-blue-main)" }}>
        <span>
          <b className="kt-font-ex-b">
            <IntlMessages id="non-face-crs.message.self-certification" />
          </b>
        </span>

        <ol className="mt-1" style={{ listStyleType: "decimal", paddingLeft: "1rem", marginBottom: 0 }}>
          <li className="mb-2">
            <IntlMessages className="kt-font-ex-b" id="non-face-crs.message.self-certification.1" />
          </li>
          <li className="mb-2">
            <IntlMessages id="non-face-crs.message.self-certification.2" />
          </li>
          <li>
            <IntlMessages id="non-face-crs.message.self-certification.3" />
          </li>
        </ol>
      </div>
    </div>
  );
};

const CountryTax = ({ country, className }) => (
  <ContectCrsFlag className={className}>
    <div>
      <div className="color-gray-secondary">
        <IntlMessages id="global.country" />
      </div>
      <div className="text-detail-main font-14">{country.countryName}</div>
    </div>
    <div className="text-end">
      <div className="color-gray-secondary">
        <IntlMessages id="global.taxId" />
      </div>
      <div className="text-detail-main font-14">{country.taxId || "-"}</div>
    </div>
  </ContectCrsFlag>
);

const ContectCrsFlag = styled.div`
  background: var(--ktb-gray-light-2);
  border-radius: 10px;
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
`;

const NonFaceCRSCase1 = styled.div`
  color: var(--ktb-navy-blue-main);
  span {
    color: var(--ktb-blue-main);
  }
  small {
  }
  p {
    color: var(--ktb-blue-main);
    text-decoration: underline;
    margin-top: 20px;
  }
  .collapse-nonface {
    span {
      color: var(--ktb-navy-blue-light-1) !important;
    }
    .ant-collapse > .ant-collapse-item > .ant-collapse-header {
      padding: 0px 0px !important;
    }
    .ant-collapse-content {
      border-top: 0px !important;
    }
    .ant-collapse > .ant-collapse-item {
      border-bottom: 0 !important;
    }
    .ant-collapse-ghost > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
      padding-top: 0px !important;
      padding-bottom: 0px !important;
    }
    .ant-collapse-content > .ant-collapse-content-box {
      padding: 0px !important;
    }
  }
`;

NonFaceCrsPopup.defaultProps = {
  selfCertType: SelfCertificationType.Property,
  messageOverrides: undefined,
};
